import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit2, Trash2, Search, Eye, Server, Cloud } from "lucide-react";
import {
  listAssetsByComponent,
  getAssetDetail,
  createAssetDeployment,
  updateAssetDeployment,
  deleteAssetDeployment,
  AssetSummaryResponse,
  AssetResponse,
  AssetRole,
  AssetStatus,
  AssetProtocol,
  CMDBComponent,
  listCiDevices,
  DeviceResponse,
  listCloudProviders,
  CloudProviderResponse,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import {
  RBACButton,
  RBACAction,
  RBACMultiAction,
} from "../components/RBACButton";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { DataTable, Column } from "../components/DataTable";
import { StatusBadge, parseOptionalPort } from "./cmdb/cmdbUtils";
import { ComponentPicker } from "./cmdb/components/ComponentPicker";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { Tip } from "../components/ui/tooltip";

// ─── Constants ──────────────────────────────────────────────────────────────

const ROLE_OPTIONS: { value: AssetRole; label: string }[] = [
  { value: "PRIMARY", label: "Primary" },
  { value: "BACKUP", label: "Backup" },
  { value: "STANDBY", label: "Standby" },
  { value: "READ_REPLICA", label: "Read Replica" },
  { value: "DR", label: "Disaster Recovery" },
  { value: "ACTIVE", label: "Active" },
  { value: "PASSIVE", label: "Passive" },
];

const STATUS_OPTIONS: { value: AssetStatus; label: string }[] = [
  { value: "RUNNING", label: "Running" },
  { value: "STOPPED", label: "Stopped" },
  { value: "STARTING", label: "Starting" },
  { value: "DEGRADED", label: "Degraded" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "UNKNOWN", label: "Unknown" },
];

const PROTOCOL_OPTIONS: { value: AssetProtocol; label: string }[] = [
  { value: "TCP", label: "TCP" },
  { value: "UDP", label: "UDP" },
  { value: "HTTP", label: "HTTP" },
  { value: "HTTPS", label: "HTTPS" },
  { value: "GRPC", label: "gRPC" },
];

// Format device type from ALL_CAPS to Title Case (e.g., SERVER -> Server, LB -> LB)
const formatDeviceType = (type: string): string => {
  if (type === "LB") return "LB";
  if (type === "VM") return "VM";
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

// ─── Form Interfaces ─────────────────────────────────────────────────────────

interface CreateForm {
  componentId: string;
  componentTitle: string;
  ciType: "ON_PREMISES" | "CLOUD";
  ciOnpremId: string;
  ciCloudProviderId: string;
  port: string;
  arn: string;
  protocol: AssetProtocol;
  role: AssetRole;
  status: AssetStatus;
  endpointUrl: string;
  notes: string;
}

const defaultCreateForm: CreateForm = {
  componentId: "",
  componentTitle: "",
  ciType: "ON_PREMISES",
  ciOnpremId: "",
  ciCloudProviderId: "",
  port: "",
  arn: "",
  protocol: "TCP",
  role: "PRIMARY",
  status: "RUNNING",
  endpointUrl: "",
  notes: "",
};

interface EditForm {
  port: string;
  arn: string;
  protocol: AssetProtocol;
  role: AssetRole;
  status: AssetStatus;
  endpointUrl: string;
  notes: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CMDBAssets() {
  const [assets, setAssets] = useState<AssetSummaryResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [ciTypeFilter, setCiTypeFilter] = useState<
    "" | "ON_PREMISES" | "CLOUD"
  >("");
  const [creating, setCreating] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Create Modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(defaultCreateForm);
  const [ciOnpremList, setCiOnpremList] = useState<DeviceResponse[]>([]);
  const [cloudProviderList, setCloudProviderList] = useState<
    CloudProviderResponse[]
  >([]);
  const [ciLoading, setCiLoading] = useState(false);

  // Edit Modal
  const [showEdit, setShowEdit] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    port: "",
    arn: "",
    protocol: "TCP",
    role: "PRIMARY",
    status: "RUNNING",
    endpointUrl: "",
    notes: "",
  });

  // Detail Modal
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<AssetResponse | null>(null);

  // Delete state
  const [deletingAsset, setDeletingAsset] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const res = await listAssetsByComponent();
      setAssets(res.data || []);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to load assets",
        );
    } finally {
      setLoading(false);
    }
  };

  // ─── Load CIs ────────────────────────────────────────────────────────────

  const loadCIOptions = async () => {
    setCiLoading(true);
    try {
      const [ciRes, cloudRes] = await Promise.allSettled([
        listCiDevices(),
        listCloudProviders(),
      ]);
      if (ciRes.status === "fulfilled") setCiOnpremList(ciRes.value.data || []);
      if (cloudRes.status === "fulfilled")
        setCloudProviderList(cloudRes.value.data || []);
    } finally {
      setCiLoading(false);
    }
  };

  const openCreate = () => {
    setCreateForm(defaultCreateForm);
    loadCIOptions();
    setShowCreate(true);
  };

  // ─── Create ───────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (creating) return;
    if (!createForm.componentId) {
      useToastStore.getState().showError("Error", "Please select a component");
      return;
    }
    const ciId =
      createForm.ciType === "ON_PREMISES"
        ? createForm.ciOnpremId
        : createForm.ciCloudProviderId;
    if (!ciId) {
      useToastStore
        .getState()
        .showError(
          "Error",
          `Please select a ${createForm.ciType === "ON_PREMISES" ? "CI" : "cloud account"}`,
        );
      return;
    }
    const port = parseOptionalPort(createForm.port);
    if (port === undefined) return;

    setCreating(true);
    try {
      const payload: Parameters<typeof createAssetDeployment>[0] = {
        componentId: createForm.componentId,
        ciType: createForm.ciType,
        protocol: createForm.protocol,
        role: createForm.role,
        status: createForm.status,
      };
      if (createForm.ciType === "ON_PREMISES")
        payload.ciOnpremId = createForm.ciOnpremId;
      else payload.ciCloudProviderId = createForm.ciCloudProviderId;
      if (port !== null) payload.port = port;
      if (createForm.arn) payload.arn = createForm.arn;
      if (createForm.endpointUrl) payload.endpointUrl = createForm.endpointUrl;
      if (createForm.notes) payload.notes = createForm.notes;

      await createAssetDeployment(payload);
      useToastStore.getState().showSuccess("Success", "Asset created");
      setShowCreate(false);
      loadAssets();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to create asset",
        );
    } finally {
      setCreating(false);
    }
  };

  // ─── Edit ────────────────────────────────────────────────────────────────

  const openEdit = (row: AssetSummaryResponse) => {
    setEditingId(row.id);
    setEditForm({
      port: row.port != null ? String(row.port) : "",
      arn: "",
      protocol: row.protocol || "TCP",
      role: row.role,
      status: row.status,
      endpointUrl: "",
      notes: "",
    });
    setShowEdit(true);
  };

  const openEditFromDetail = (asset: AssetResponse) => {
    setShowDetail(false);
    setEditingId(asset.id);
    setEditForm({
      port: asset.port != null ? String(asset.port) : "",
      arn: asset.arn || "",
      protocol: asset.protocol,
      role: asset.role,
      status: asset.status,
      endpointUrl: asset.endpointUrl || "",
      notes: asset.notes || "",
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    if (savingEdit) return;
    if (!editingId) return;
    const port = parseOptionalPort(editForm.port);
    if (port === undefined) return;

    setSavingEdit(true);
    try {
      await updateAssetDeployment(editingId, {
        port,
        arn: editForm.arn || null,
        protocol: editForm.protocol,
        role: editForm.role,
        status: editForm.status,
        endpointUrl: editForm.endpointUrl || null,
        notes: editForm.notes || null,
      });
      useToastStore.getState().showSuccess("Success", "Asset updated");
      setShowEdit(false);
      loadAssets();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to update asset",
        );
    } finally {
      setSavingEdit(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = (id: string, name: string) => {
    setDeletingAsset({ id, name });
  };

  const confirmDelete = async () => {
    if (!deletingAsset) return;
    setIsDeleting(true);
    try {
      await deleteAssetDeployment(deletingAsset.id);
      useToastStore.getState().showSuccess("Success", "Asset deleted");
      setDeletingAsset(null);
      setShowDetail(false);
      loadAssets();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Delete failed",
        );
    } finally {
      setIsDeleting(false);
    }
  };

  // ─── Detail ───────────────────────────────────────────────────────────────

  const openDetail = async (id: string) => {
    setDetail(null);
    setShowDetail(true);
    setDetailLoading(true);
    try {
      const res = await getAssetDetail(id);
      setDetail(res.data || null);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to load detail",
        );
      setShowDetail(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // ─── Filter ───────────────────────────────────────────────────────────────

  const filtered = assets.filter((a) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      a.componentTitle.toLowerCase().includes(q) ||
      (a.ciName || "").toLowerCase().includes(q);
    const matchesType = !ciTypeFilter || a.ciType === ciTypeFilter;
    return matchesSearch && matchesType;
  });

  // ─── Table Columns ────────────────────────────────────────────────────────

  const columns: Column<AssetSummaryResponse>[] = [
    {
      key: "_sno",
      header: "#",
      width: "w-10",
      align: "center",
      render: (_, i) => <span className="d text-sm">{i + 1}</span>,
    },
    {
      key: "componentTitle",
      header: "Component",
      align: "left",
      render: (row) => (
        <span className="font-medium">{row.componentTitle}</span>
      ),
    },
    {
      key: "ciName",
      header: "Infrastructure",
      align: "left",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.ciType === "ON_PREMISES" ? (
            <Server className="size-4 d flex-shrink-0" />
          ) : (
            <Cloud className="size-4 d flex-shrink-0" />
          )}
          <div>
            <p className="text-sm font-medium">{row.ciName || "-"}</p>
            <p className="text-xs d">
              {row.ciType === "ON_PREMISES" ? "On-Premises" : "Cloud"}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "port",
      header: "Port",
      align: "center",
      render: (row) => (
        <span className="font-mono text-sm d">{row.port ?? "-"}</span>
      ),
    },
    {
      key: "protocol",
      header: "Protocol",
      align: "center",
      render: (row) => (
        <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
          {row.protocol || "-"}
        </span>
      ),
    },
    {
      key: "role",
      header: "Role",
      align: "center",
      render: (row) => <StatusBadge status={row.role} type="role" />,
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "_actions",
      header: "Actions",
      align: "center",
      width: "w-28",
      render: (row) => (
        <div className="flex items-center justify-center gap-1">
          <Tip label="View Detail" side="top">
            <button
              onClick={() => openDetail(row.id)}
              className="p-1.5 hover:bg-muted rounded transition-colors"
            >
              <Eye className="size-4 d hover:text-foreground" />
            </button>
          </Tip>
          <RBACAction module="cmdb" action="update">
            <Tip label="Edit" side="top">
              <button
                onClick={() => openEdit(row)}
                className="p-1.5 hover:bg-muted rounded transition-colors"
              >
                <Edit2 className="size-4 d hover:text-foreground" />
              </button>
            </Tip>
          </RBACAction>
          <RBACAction module="cmdb" action="delete">
            <Tip label="Delete" side="top">
              <button
                onClick={() =>
                  handleDelete(row.id, row.ciName || row.componentTitle)
                }
                className="p-1.5 hover:bg-muted rounded transition-colors"
              >
                <Trash2 className="size-4 d hover:text-red-600" />
              </button>
            </Tip>
          </RBACAction>
        </div>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Assets</h1>
          <p className="text-sm d mt-0.5">
            Reusable assets linking components to infrastructure — shared across
            multiple applications
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
          <input
            type="text"
            placeholder="Search by component or CI name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none"
          />
        </div>

        <div className="flex rounded-lg border border-border overflow-hidden">
          {[
            { value: "" as const, label: "All" },
            { value: "ON_PREMISES" as const, label: "On-Premises" },
            { value: "CLOUD" as const, label: "Cloud" },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setCiTypeFilter(opt.value)}
              className={`px-3 py-2 text-sm transition-colors ${
                ciTypeFilter === opt.value
                  ? "bg-indigo-600 text-white"
                  : "bg-background d hover:bg-muted"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <RBACMultiAction
          checks={[
            ["cmdb", "create"],
            ["cmdb", "update"],
          ]}
          type="any"
        >
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors ml-auto"
          >
            <Plus className="size-4" />
            Add New
          </button>
        </RBACMultiAction>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        emptyText="No assets found"
      />

      {/* ─── Create Modal ─────────────────────────────────────────────────────── */}
      <Modal
        open={showCreate}
        onClose={() => {
          if (!creating) setShowCreate(false);
        }}
        title="Create Asset"
        size="lg"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowCreate(false)}
            onSubmit={handleCreate}
            submitLabel={creating ? "Saving" : "Save"}
            submitDisabled={creating}
          />
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Component" required>
              <ComponentPicker
                value={createForm.componentId}
                title={createForm.componentTitle}
                onChange={(id, title) =>
                  setCreateForm({
                    ...createForm,
                    componentId: id,
                    componentTitle: title,
                  })
                }
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="CI Type" required>
              <FormSelect
                value={createForm.ciType}
                onChange={(v) =>
                  setCreateForm({
                    ...createForm,
                    ciType: v as "ON_PREMISES" | "CLOUD",
                    ciOnpremId: "",
                    ciCloudProviderId: "",
                  })
                }
                options={[
                  { value: "ON_PREMISES", label: "On-Premises CI" },
                  { value: "CLOUD", label: "Cloud Account" },
                ]}
              />
            </FormField>

            {createForm.ciType === "ON_PREMISES" ? (
              <FormField label="On-Premises CI" required>
                <FormSelect
                  value={createForm.ciOnpremId}
                  onChange={(v) =>
                    setCreateForm({ ...createForm, ciOnpremId: v })
                  }
                  options={[
                    { value: "", label: ciLoading ? "Loading" : "Select CI" },
                    ...ciOnpremList.map((ci) => ({
                      value: ci.id,
                      label: `${ci.name} (${ci.ipAddress})`,
                    })),
                  ]}
                />
              </FormField>
            ) : (
              <FormField label="Cloud Account" required>
                <FormSelect
                  value={createForm.ciCloudProviderId}
                  onChange={(v) =>
                    setCreateForm({ ...createForm, ciCloudProviderId: v })
                  }
                  options={[
                    {
                      value: "",
                      label: ciLoading ? "Loading" : "Select cloud account",
                    },
                    ...cloudProviderList.map((p) => ({
                      value: p.id,
                      label: `${p.accountName} (${p.provider} — ${p.region})`,
                    })),
                  ]}
                />
              </FormField>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Port">
              <input
                type="number"
                value={createForm.port}
                onChange={(e) =>
                  setCreateForm({ ...createForm, port: e.target.value })
                }
                placeholder="e.g. 3306"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none"
              />
            </FormField>

            <FormField label="Protocol" required>
              <FormSelect
                value={createForm.protocol}
                onChange={(v) =>
                  setCreateForm({ ...createForm, protocol: v as AssetProtocol })
                }
                options={PROTOCOL_OPTIONS}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Role" required>
              <FormSelect
                value={createForm.role}
                onChange={(v) =>
                  setCreateForm({ ...createForm, role: v as AssetRole })
                }
                options={ROLE_OPTIONS}
              />
            </FormField>

            <FormField label="Status" required>
              <FormSelect
                value={createForm.status}
                onChange={(v) =>
                  setCreateForm({ ...createForm, status: v as AssetStatus })
                }
                options={STATUS_OPTIONS}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Endpoint URL">
              <FormInput
                value={createForm.endpointUrl}
                onChange={(v) =>
                  setCreateForm({ ...createForm, endpointUrl: v })
                }
                placeholder="e.g. jdbc:mysql://host:3306/db"
              />
            </FormField>

            <FormField label="ARN">
              <FormInput
                value={createForm.arn}
                onChange={(v) => setCreateForm({ ...createForm, arn: v })}
                placeholder="e.g. arn:aws:rds:ap-southeast-1:"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Notes">
              <FormInput
                value={createForm.notes}
                onChange={(v) => setCreateForm({ ...createForm, notes: v })}
                placeholder="Optional notes"
              />
            </FormField>
          </div>
        </div>
      </Modal>

      {/* ─── Edit Modal ───────────────────────────────────────────────────────── */}
      <Modal
        open={showEdit}
        onClose={() => {
          if (!savingEdit) setShowEdit(false);
        }}
        title="Edit Asset"
        size="lg"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowEdit(false)}
            onSubmit={handleSaveEdit}
            submitLabel={savingEdit ? "Saving" : "Save"}
            submitDisabled={savingEdit}
          />
        }
      >
        <div className="space-y-4">
          <p className="text-xs d bg-muted/50 border border-border rounded-lg px-3 py-2">
            Component, CI type, and infrastructure cannot be changed after the
            asset is created.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Port">
              <input
                type="number"
                value={editForm.port}
                onChange={(e) =>
                  setEditForm({ ...editForm, port: e.target.value })
                }
                placeholder="e.g. 3306"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none"
              />
            </FormField>

            <FormField label="Protocol" required>
              <FormSelect
                value={editForm.protocol}
                onChange={(v) =>
                  setEditForm({ ...editForm, protocol: v as AssetProtocol })
                }
                options={PROTOCOL_OPTIONS}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Role" required>
              <FormSelect
                value={editForm.role}
                onChange={(v) =>
                  setEditForm({ ...editForm, role: v as AssetRole })
                }
                options={ROLE_OPTIONS}
              />
            </FormField>

            <FormField label="Status" required>
              <FormSelect
                value={editForm.status}
                onChange={(v) =>
                  setEditForm({ ...editForm, status: v as AssetStatus })
                }
                options={STATUS_OPTIONS}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Endpoint URL">
              <FormInput
                value={editForm.endpointUrl}
                onChange={(v) => setEditForm({ ...editForm, endpointUrl: v })}
                placeholder="e.g. jdbc:mysql://host:3306/db"
              />
            </FormField>

            <FormField label="ARN">
              <FormInput
                value={editForm.arn}
                onChange={(v) => setEditForm({ ...editForm, arn: v })}
                placeholder="e.g. arn:aws:rds:"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Notes">
              <FormInput
                value={editForm.notes}
                onChange={(v) => setEditForm({ ...editForm, notes: v })}
                placeholder="Optional notes"
              />
            </FormField>
          </div>
        </div>
      </Modal>

      {/* ─── Detail Modal ─────────────────────────────────────────────────────── */}
      <Modal
        open={showDetail}
        onClose={() => setShowDetail(false)}
        title="Asset Detail"
        size="sm"
        footer={
          detail ? (
            <div className="flex justify-between w-full">
              <button
                onClick={() =>
                  handleDelete(
                    detail.id,
                    detail.ciOnprem?.name ||
                      detail.ciCloud?.accountName ||
                      detail.component.title,
                  )
                }
                className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg transition-colors text-sm"
              >
                Delete
              </button>
              <button
                onClick={() => openEditFromDetail(detail)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                Edit
              </button>
            </div>
          ) : undefined
        }
      >
        {detailLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="size-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : detail ? (
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4">
              <p className="text-xs font-semibold d tracking-wider mb-2">
                Component
              </p>
              <p className="font-medium">{detail.component.title}</p>
              <p className="text-sm d mt-0.5">
                {detail.component.componentType || "SOFTWARE"}
              </p>
            </div>

            <div className="border border-border rounded-lg p-4">
              <p className="text-xs font-semibold d tracking-wider mb-2">
                Infrastructure (
                {detail.ciType === "ON_PREMISES" ? "On-Premises" : "Cloud"})
              </p>
              {detail.ciType === "ON_PREMISES" && detail.ciOnprem ? (
                <div>
                  <p className="font-medium">{detail.ciOnprem.name}</p>
                  <p className="text-sm d mt-0.5">
                    {detail.ciOnprem.ipAddress}
                    {detail.ciOnprem.hostname
                      ? ` • ${detail.ciOnprem.hostname}`
                      : ""}
                  </p>
                  <p className="text-xs d mt-0.5">
                    {formatDeviceType(detail.ciOnprem.type)}
                    {detail.ciOnprem.osName
                      ? ` • ${detail.ciOnprem.osName} ${detail.ciOnprem.osVersion || ""}`
                      : ""}
                  </p>
                </div>
              ) : detail.ciType === "CLOUD" && detail.ciCloud ? (
                <div>
                  <p className="font-medium">{detail.ciCloud.accountName}</p>
                  <p className="text-sm d mt-0.5">
                    {detail.ciCloud.provider} • {detail.ciCloud.region}
                  </p>
                  <p className="text-xs font-mono d mt-0.5">
                    {detail.ciCloud.accountId}
                  </p>
                </div>
              ) : (
                <p className="text-sm d">No CI details available</p>
              )}
            </div>

            {(detail.cloudResource ?? detail.awsResource) && (
              <div className="border border-border rounded-lg p-4">
                <p className="text-xs font-semibold d tracking-wider mb-2">
                  Cloud Resource
                </p>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-xs d">Type</span>
                    <span className="text-sm font-medium">
                      {
                        (detail.cloudResource ?? detail.awsResource)
                          ?.serviceName
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs d">ID</span>
                    <span className="text-xs font-mono text-foreground">
                      {(detail.cloudResource ?? detail.awsResource)?.resourceId}
                    </span>
                  </div>
                  {(detail.cloudResource ?? detail.awsResource)
                    ?.resourceName && (
                    <div className="flex justify-between">
                      <span className="text-xs d">Name</span>
                      <span className="text-sm text-foreground">
                        {
                          (detail.cloudResource ?? detail.awsResource)
                            ?.resourceName
                        }
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-xs d">Region</span>
                    <span className="text-sm text-foreground">
                      {(detail.cloudResource ?? detail.awsResource)?.region}
                    </span>
                  </div>
                  {(detail.cloudResource ?? detail.awsResource)
                    ?.availabilityZone && (
                    <div className="flex justify-between">
                      <span className="text-xs d">AZ</span>
                      <span className="text-sm text-foreground">
                        {
                          (detail.cloudResource ?? detail.awsResource)
                            ?.availabilityZone
                        }
                      </span>
                    </div>
                  )}
                  {(detail.cloudResource ?? detail.awsResource)?.status && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs d">Status</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded font-medium ${
                          ["running", "available", "active"].includes(
                            (
                              (detail.cloudResource ?? detail.awsResource)
                                ?.status || ""
                            ).toLowerCase(),
                          )
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                            : ["stopped", "failed"].includes(
                                  (
                                    (detail.cloudResource ?? detail.awsResource)
                                      ?.status || ""
                                  ).toLowerCase(),
                                )
                              ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400"
                        }`}
                      >
                        {(detail.cloudResource ?? detail.awsResource)?.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="border border-border rounded-lg p-3">
                <p className="text-xs d mb-1">Port</p>
                <p className="font-mono font-medium">{detail.port ?? "-"}</p>
              </div>
              <div className="border border-border rounded-lg p-3">
                <p className="text-xs d mb-1">Protocol</p>
                <p className="font-mono font-medium">{detail.protocol}</p>
              </div>
              <div className="border border-border rounded-lg p-3">
                <p className="text-xs d mb-1">Role</p>
                <StatusBadge status={detail.role} type="role" />
              </div>
              <div className="border border-border rounded-lg p-3">
                <p className="text-xs d mb-1">Status</p>
                <StatusBadge status={detail.status} />
              </div>
            </div>

            {(detail.endpointUrl || detail.arn || detail.notes) && (
              <div className="border border-border rounded-lg p-4 space-y-3">
                {detail.endpointUrl && (
                  <div>
                    <p className="text-xs d mb-0.5">Endpoint URL</p>
                    <p className="text-sm font-mono break-all">
                      {detail.endpointUrl}
                    </p>
                  </div>
                )}
                {detail.arn && (
                  <div>
                    <p className="text-xs d mb-0.5">ARN</p>
                    <p className="text-sm font-mono break-all">{detail.arn}</p>
                  </div>
                )}
                {detail.notes && (
                  <div>
                    <p className="text-xs d mb-0.5">Notes</p>
                    <p className="text-sm">{detail.notes}</p>
                  </div>
                )}
              </div>
            )}

            <div className="text-xs d space-y-1">
              <p>Created: {new Date(detail.createdAt).toLocaleString()}</p>
              <p>Updated: {new Date(detail.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        ) : null}
      </Modal>
      <DeleteConfirmDialog
        open={!!deletingAsset}
        type="Asset"
        name={deletingAsset?.name || ""}
        deleting={isDeleting}
        onCancel={() => setDeletingAsset(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
